import React, {
  FC,
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useSetFocus } from '@please/lrud'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { updateChannelEvents } from '@/actions/channelEvents'
import { ChannelVideoDebugInfo } from '@/components/ChannelVideoDebugInfo/ChannelVideoDebugInfo'
import {
  useVideoOverlay,
  VideoOverlayTypes,
} from '@/components/VideoPlayer/hooks/useVideoOverlay'
import { eventAction, eventObject } from '@/constants/analytics'
import { focusIds } from '@/constants/focus'
import { useToggleFullscreenFocus } from '@/hooks/useToggleFullscreenFocus'
import useStore from '@/store/useStore'
import { VideoViewedType } from '@/types/analyticsTypes'
import { VideoPlayerProps } from '@/types/componentsPropsTypes'
import { eventTypes, MediaContentType } from '@/types/servicesTypes/configTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'
import { getVideoForChannel } from '@/utils/utils'
import { replaceVideoUrlParams } from '@/utils/videoParamsUtils'

const VideoPlayer: FC<VideoPlayerProps> = lazy(
  () => import('@/components/VideoPlayer/VideoPlayer'),
)

import { MediaPlayerBackground, MediaPlayerWrapper } from '../styles'

type Props = { mediaVideo: MediaContentType }

const MediaPlayerRaw: FC<Props> = ({ mediaVideo }) => {
  const setFocus = useSetFocus()
  const setListPopup = useStore((state) => state.common.setListPopup)
  const { isRegisteredUser } = useAuth()
  const isAppHidden = useStore((state) => state.common.isAppHidden)
  const [videoData, setVideoData] = useState<VideoViewedType>()
  const { videoControlsVisible, showVideoControls } = useStore(
    (state) => state.player,
  )
  const setMediaVideo = useStore((state) => state.common.setMediaVideo)

  const isChannelType = mediaVideo.contentType === eventTypes.CHANNEL
  const activeChannel = mediaVideo.channel
  const isVod = videoData?.type === eventTypes.VOD

  const channelEvent = useStore(
    (state) => state.common.channelEvents[activeChannel?.id || ''],
  )
  const currentChannelEvent = channelEvent?.isFutureChannelEvent
    ? null
    : channelEvent

  const { isAccessible, isRequiresRegistrationOnly } = useMemo(() => {
    return getContentAccessFlags(
      currentChannelEvent,
      activeChannel,
      isRegisteredUser,
    )
  }, [currentChannelEvent, activeChannel, isRegisteredUser])

  const { videoOverlay, setVideoOverlay } = useVideoOverlay({
    activeChannel,
    isChannelVideoType: isChannelType,
  })

  const hasVideoBlockedOverlay =
    videoOverlay?.overlayType == VideoOverlayTypes.BLOCKED

  useEffect(() => {
    if (!activeChannel) {
      return
    }
    updateChannelEvents(isAppHidden, [activeChannel])
  }, [activeChannel, isAppHidden])

  const getMediaData = useCallback(async () => {
    let mediaVideoData
    const mediaVideoSources = mediaVideo.sources?.[0]
    if (activeChannel) {
      const isChannelWatchable = !!activeChannel.watchable

      mediaVideoData = await getVideoForChannel(
        activeChannel,
        isAccessible,
        isChannelWatchable,
        hasVideoBlockedOverlay,
        currentChannelEvent,
      )
    } else if (mediaVideoSources) {
      let videoFormat
      if (mediaVideoSources && 'format' in mediaVideoSources) {
        videoFormat = mediaVideoSources.format
      } else {
        videoFormat = mediaVideoSources?.type
      }
      mediaVideoData = {
        src: mediaVideoSources.url,
        format: videoFormat,
        onEnded: mediaVideo.onEnded,
        adSettings: mediaVideo.adSettings,
      }
    }

    if (mediaVideoData) {
      const videoUrl = replaceVideoUrlParams({
        url: mediaVideoData.src,
        isVod,
      })
      const videoData: VideoViewedType = {
        id: mediaVideo.id,
        type: mediaVideo.contentType || 'Media has no type',
        media_name: mediaVideo.name,
        ...mediaVideoData,
        partnerIds: mediaVideo.partnerIds || mediaVideoData.partnerIds,
        url: videoUrl,
      }
      isDevMode && console.log('[MediaPlayer] videoData', videoData)
      setVideoData(videoData)
      return
    }
  }, [
    mediaVideo,
    activeChannel,
    isAccessible,
    hasVideoBlockedOverlay,
    currentChannelEvent,
  ])

  useEffect(() => {
    getMediaData()
  }, [mediaVideo, activeChannel, isAccessible, hasVideoBlockedOverlay])

  useToggleFullscreenFocus({ mediaContent: mediaVideo })

  const onKey = (e: LRUDEvent) => {
    if (!videoControlsVisible) {
      e.stopPropagation()
      e.preventDefault()
      showVideoControls()
    }
  }

  const isBlocked = activeChannel && !isAccessible

  const videoExitHandler = () => {
    setMediaVideo(null)

    if (mediaVideo && mediaVideo.listOptions) {
      const listContextId = mediaVideo.listOptions.listContextId
      setListPopup(mediaVideo.listOptions)
      setListPopup(null)
      if (listContextId) {
        setFocus(listContextId)
      } else {
        setFocus(focusIds.CONTENT)
      }
    }

    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.BACKGROUND_CLICK,
      video: {
        id: videoData?.id || mediaVideo.id,
        link: videoData?.url,
      },
    })
  }

  // return null

  return (
    <MediaPlayerBackground $isPopup data-component="MediaPlayer">
      <MediaPlayerWrapper
        focusId={focusIds.FULLSCREEN_PLAYER}
        onKey={onKey}
        defaultFocusChild={1}
        isTrap
      >
        {isDevMode && isChannelType && (
          <ChannelVideoDebugInfo
            eventChannel={activeChannel}
            event={channelEvent}
            mediaVideo={mediaVideo}
          />
        )}
        {videoData && (
          <Suspense>
            <VideoPlayer
              src={videoData.url}
              videoData={videoData}
              autoplay={true}
              onEndedAction={videoData.onEnded}
              onExitButton={videoExitHandler}
              isBlocked={isBlocked}
              channelFromProps={activeChannel}
              currentEvent={channelEvent}
              isStandalone
              adSettings={videoData.adSettings}
              videoOverlay={videoOverlay}
              setVideoOverlay={setVideoOverlay}
              search={mediaVideo.redirectSearch}
              isRequiresRegistrationOnly={isRequiresRegistrationOnly}
            />
          </Suspense>
        )}
      </MediaPlayerWrapper>
    </MediaPlayerBackground>
  )
}

export const MediaPlayer = memo(MediaPlayerRaw)
